import React, { useState } from "react";
import { State } from "../types";
import { Habit, Context, Category } from "../types";
import { Action } from "../hooks/useHabitState";
import { SettingsTabButton } from "../components/SettingsTabButton";
import { HabitsSettingsSection } from "../components/HabitsSettingsSection";
import { ContextsSettingsSection } from "../components/ContextsSettingsSection";
import { CategoriesSettingsSection } from "../components/CategoriesSettingsSection";
import { FeaturesSettingsSection } from "../components/FeaturesSettingsSection";
import { DataSettingsSection } from "../components/DataSettingsSection";
import { useDataActions } from "../hooks/useDataActions";

interface SettingsViewProps {
  state: State;
  dispatch: React.Dispatch<Action>;
  onAddHabit: () => void;
  onEditHabit: (habit: Habit) => void;
  onAddContext: () => void;
  onEditContext: (context: Context) => void;
  onAddCategory: () => void;
  onEditCategory: (category: Category) => void;
  onToggleGreyOut: (value: boolean) => void;
  onToggleContexts: (value: boolean) => void;
  onToggleNotes: (value: boolean) => void;
  onToggleCategories: (value: boolean) => void;
  onToggleHeatmap: (value: boolean) => void;
  onSortOrderChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

export const SettingsView: React.FC<SettingsViewProps> = ({
  state,
  dispatch,
  onAddHabit,
  onEditHabit,
  onAddContext,
  onEditContext,
  onAddCategory,
  onEditCategory,
  onToggleGreyOut,
  onToggleContexts,
  onToggleNotes,
  onToggleCategories,
  onToggleHeatmap,
  onSortOrderChange,
}) => {
  const [settingsTab, setSettingsTab] = useState<"habits" | "features" | "data">("habits");
  const dataActions = useDataActions(dispatch);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingBottom: "60px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>Settings</h2>
      </div>

      <div style={{
        display: "flex",
        gap: "8px",
        padding: "4px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
      }}>
        <SettingsTabButton
          tab="habits"
          label="Daily"
          isActive={settingsTab === "habits"}
          onClick={setSettingsTab}
        />
        <SettingsTabButton
          tab="features"
          label="Features"
          isActive={settingsTab === "features"}
          onClick={setSettingsTab}
        />
        <SettingsTabButton
          tab="data"
          label="Data"
          isActive={settingsTab === "data"}
          onClick={setSettingsTab}
        />
      </div>

      {settingsTab === "habits" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <HabitsSettingsSection
            habits={state.habits}
            onAddHabit={onAddHabit}
            onEditHabit={onEditHabit}
          />

          <ContextsSettingsSection
            contexts={state.contexts}
            onAddContext={onAddContext}
            onEditContext={onEditContext}
          />

          {state.categoriesEnabled && (
            <CategoriesSettingsSection
              categories={state.categories}
              onAddCategory={onAddCategory}
              onEditCategory={onEditCategory}
            />
          )}
        </div>
      )}

      {settingsTab === "features" && (
        <FeaturesSettingsSection
          greyOutEnabled={state.greyOutEnabled}
          contextsEnabled={state.contextsEnabled}
          notesEnabled={state.notesEnabled}
          categoriesEnabled={state.categoriesEnabled}
          heatmapEnabled={state.heatmapEnabled}
          sortOrder={state.sortOrder}
          onToggleGreyOut={onToggleGreyOut}
          onToggleContexts={onToggleContexts}
          onToggleNotes={onToggleNotes}
          onToggleCategories={onToggleCategories}
          onToggleHeatmap={onToggleHeatmap}
          onSortOrderChange={onSortOrderChange}
        />
      )}

      {settingsTab === "data" && (
        <DataSettingsSection
          onExportData={() => dataActions.handleExportData(state)}
          onImportData={dataActions.handleImportData}
          onResetData={dataActions.handleResetConfirm}
        />
      )}
    </div>
  );
};